// filepath: apps/web/src/renderers/form/fields/DateTimeField.tsx
//
// Date+time input (no timezone). Uses `<input type="datetime-local">`,
// which stores the value as `YYYY-MM-DDTHH:mm` (local time, no zone).

import React from "react";
import type { FieldComponentProps } from "../FieldRegistry";
import { getUiSchemaField } from "../FieldRegistry";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export function DateTimeField({
  fieldId,
  value,
  onChange,
  disabled,
  uiSchema,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  const helpText = resolveLocalizedValue(uiField?.help, locale, "");
  const helpId = `field-help-${fieldId}`;
  const describedBy = helpText ? helpId : undefined;

  const labelStyle: React.CSSProperties = isMobile
    ? {
        display: "block",
        marginBottom: "4px",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        lineHeight: "1.4",
      }
    : {};

  const inputStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        fontSize: "16px",
        padding: "8px 12px",
        boxSizing: "border-box",
      }
    : {};

  const helpStyle: React.CSSProperties = isMobile
    ? {
        fontSize: "14px",
        marginTop: "4px",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }
    : {};

  return (
    <div data-testid={`field-${fieldId}`} className="field field-datetime">
      <label htmlFor={fieldId} style={labelStyle}>
        {labelText}
        {uiField?.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={fieldId}
        type="datetime-local"
        name={fieldId}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        aria-required={uiField?.required ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={`input-${fieldId}`}
        style={inputStyle}
      />
      {helpText ? (
        <p id={helpId} className="field-help" style={helpStyle}>
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
